import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined'
import EventOutlinedIcon from '@mui/icons-material/EventOutlined'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined'
import TodayOutlinedIcon from '@mui/icons-material/TodayOutlined'
import DateRangeOutlinedIcon from '@mui/icons-material/DateRangeOutlined'
import { scoreColour } from 'features/dashboard/charts/scoreColour'
import { NOT_ENOUGH_DATA } from 'features/actions/workspace/remedialPerformance'
import { SkeletonRows } from 'components/Skeleton'
import InfiniteScrollSentinel from 'components/InfiniteScrollSentinel'
import ConvertToTaskDialog from 'features/actions/components/ConvertToTaskDialog'
import ActionsAPI from 'services/ActionsAPI'
import { getMyWorkSummary, getMyPerformance } from 'services/workSummaryService'
import { getCurrentUser } from 'features/auth/ducks/selectors'
import TrendBars from 'features/dashboard/sections/TrendBars'
import { EMPTY_FILTERS, buildListParams, remedialsViewUrl } from 'features/actions/workspace/remedialsQuery'
import { dueInfo } from 'features/actions/workspace/remedialDates'
import { severityMeta } from 'features/actions/workspace/remedialMeta'

const formatDate = iso => iso ? new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '—'

const pad = n => String(n).padStart(2, '0')
const ymdOffset = days => {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  d.setDate(d.getDate() + days)
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

const healthStatusFor = rate => {
  if (rate === null || rate === undefined) return null
  if (rate >= 80) return { label: 'On track', fg: '#15803d', bg: '#dcfce7' }
  if (rate >= 50) return { label: 'Keep an eye on it', fg: '#d97706', bg: '#fef3c7' }
  return { label: 'Falling behind', fg: '#b00020', bg: '#fdecea' }
}

const PAGE = 10

const TeamMemberView = () => {
  const navigate = useNavigate()
  const currentUser = useSelector(getCurrentUser)
  const uid = currentUser?.id
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [perf, setPerf] = useState(null)
  const [perfLoading, setPerfLoading] = useState(true)
  const [todayVisible, setTodayVisible] = useState(PAGE)

  const [remedials, setRemedials] = useState([])
  const [remMeta, setRemMeta] = useState({ currentPage: 0, lastPage: 1, total: null })
  const [remLoading, setRemLoading] = useState(true)
  const [convertTarget, setConvertTarget] = useState(null)

  useEffect(() => {
    let cancelled = false
    getMyWorkSummary()
      .then(res => { if (!cancelled) setData(res?.data?.data || null) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    let cancelled = false
    getMyPerformance()
      .then(res => { if (!cancelled) setPerf(res?.data?.data || null) })
      .finally(() => { if (!cancelled) setPerfLoading(false) })
    return () => { cancelled = true }
  }, [])

  const fetchRemedials = useCallback(async page => {
    setRemLoading(true)
    try {
      const res = await ActionsAPI.list(buildListParams({ scope: { kind: 'all', id: null }, view: 'mine', filters: EMPTY_FILTERS, sort: 'due_asc', page, perPage: 25 }))
      const incoming = res?.data?.data || []
      const meta = res?.data?.meta || {}
      setRemedials(prev => page > 1
        ? [...prev, ...incoming.filter(i => !prev.some(x => x.id === i.id))]
        : incoming)
      setRemMeta({ currentPage: meta.currentPage ?? page, lastPage: meta.lastPage ?? page, total: meta.total ?? incoming.length })
    } finally {
      setRemLoading(false)
    }
  }, [])

  useEffect(() => { fetchRemedials(1) }, [fetchRemedials])

  const onConverted = id => {
    setRemedials(prev => prev.filter(r => r.id !== id))
    setRemMeta(m => ({ ...m, total: m.total != null ? Math.max(0, m.total - 1) : m.total }))
    setConvertTarget(null)
  }

  if (loading) return <SkeletonRows rows={6} widths={['40%', '20%', '16%']} />

  const health = data?.personalHealth || {}
  const awaitingSignoff = data?.awaitingSignoff || []
  const today = data?.today || []
  const week = data?.thisWeek || []
  const month = data?.thisMonth || []
  const openTasksTotal = data?.openTasksTotal ?? 0
  const perfRate = perf?.onTimeRate
  const perfTint = scoreColour(perfRate)
  const perfStatus = healthStatusFor(perfRate)
  const team = perf?.team || {}
  const showRank = !!(team.size > 1 && team.rank)
  const showVs = !!(team.size > 1 && team.myOnTimeRate != null && team.medianOnTimeRate != null)
  const aboveMedian = team.myOnTimeRate != null && team.medianOnTimeRate != null && team.myOnTimeRate >= team.medianOnTimeRate

  const openTask = id => navigate(`/tasks/${id}`)
  const openRemedial = id => navigate(`/actions/${id}`)

  const stats = [
    {
      label: 'Open tasks',
      value: openTasksTotal,
      icon: AssignmentOutlinedIcon,
      tint: '#33becc',
      to: `/tasks?assignee=${uid}&open=1`,
      aria: 'View my open tasks',
    },
    {
      label: 'Due this week',
      value: week.length,
      icon: EventOutlinedIcon,
      tint: '#0891b2',
      to: `/tasks?assignee=${uid}&open=1&deadline_from=${ymdOffset(1)}&deadline_to=${ymdOffset(7)}`,
      aria: 'View my tasks due this week',
    },
    {
      label: 'Overdue now',
      value: health.overdueOpen ?? 0,
      icon: ErrorOutlineRoundedIcon,
      tint: '#b00020',
      tone: (health.overdueOpen ?? 0) > 0 ? 'bad' : null,
      to: `/tasks?assignee=${uid}&open=1&overdue=1`,
      aria: 'View my overdue tasks',
    },
    {
      label: 'Awaiting your sign-off',
      value: awaitingSignoff.length,
      icon: FactCheckOutlinedIcon,
      tint: '#d97706',
      to: `/tasks?approver=${uid}&status=awaiting_approval`,
      aria: 'View tasks awaiting your sign-off',
    },
    {
      label: 'My remedials',
      value: remMeta.total ?? '—',
      icon: BoltOutlinedIcon,
      tint: '#D24724',
      to: remedialsViewUrl({}, 'mine'),
      aria: 'View remedials you own or have a task for',
    },
  ]

  const TaskRow = t => (
    <Item key={t.id} as='button' type='button' $overdue={t.overdue} onClick={() => openTask(t.id)}>
      <ItemMain>
        <ItemTitle>{t.title}</ItemTitle>
        <ItemMeta>{[t.buildingName, t.raisedByMe ? 'raised by you' : (t.raisedBy && `raised by ${t.raisedBy}`)].filter(Boolean).join(' · ')}</ItemMeta>
      </ItemMain>
      <Due $overdue={t.overdue}>{t.overdue ? 'Overdue' : 'Due'} {formatDate(t.dueAt)}</Due>
    </Item>
  )

  return (
    <Grid>
      <TopBar>
        <CreateTask type='button' onClick={() => navigate('/tasks/create')}>
          <AddRoundedIcon style={{ fontSize: 18 }} /> Create task
        </CreateTask>
      </TopBar>
      <Stats>
        {stats.map(s => {
          const Icon = s.icon
          return (
            <StatTile key={s.label} as='button' type='button' onClick={() => navigate(s.to)} aria-label={s.aria}>
              <IconChip $tint={s.tint}><Icon style={{ fontSize: 20 }} /></IconChip>
              <StatText>
                <StatValue $tone={s.tone}>{s.value}</StatValue>
                <StatLabel>{s.label}</StatLabel>
              </StatText>
              <Chevron><ChevronRightRoundedIcon style={{ fontSize: 20 }} /></Chevron>
            </StatTile>
          )
        })}
      </Stats>

      <Panel>
        <PanelHead><InsightsOutlinedIcon style={{ fontSize: 16 }} />My performance</PanelHead>
        {perfLoading ? (
          <SkeletonRows rows={4} widths={['45%', '80%', '60%', '50%']} />
        ) : !perf || (perfRate == null && (perf.trend || []).every(p => p.onTimeRate == null)) ? (
          <Empty>Not enough completed tasks yet to show your performance. Once you complete a few, this fills in with your on-time trend and how you compare.</Empty>
        ) : (
          <PerfBody>
            <PerfHero>
              <Big $tint={perfTint}>{perfRate == null ? 'No completed tasks yet' : `${Math.round(perfRate)}%`}</Big>
              <HeroMeta>
                <HeroLabel>on-time rate</HeroLabel>
                {perfStatus && <StatusPill $fg={perfStatus.fg} $bg={perfStatus.bg}>{perfStatus.label}</StatusPill>}
              </HeroMeta>
              {showRank && (
                <RankBadge $good={aboveMedian}>
                  #{team.rank} of {team.size} · {aboveMedian ? 'above' : 'below'} team median
                </RankBadge>
              )}
            </PerfHero>

            <TrendBars points={perf.trend || []} />

            <StatCells>
              <Cell>
                <CellValue>{perf.tasksCompletedOnTime ?? 0}</CellValue>
                <CellLabel>done on time</CellLabel>
              </Cell>
              <Cell>
                <CellValue>{perf.avgDaysToComplete == null ? NOT_ENOUGH_DATA : perf.avgDaysToComplete}</CellValue>
                <CellLabel>avg days to complete</CellLabel>
              </Cell>
              <Cell>
                <CellValue>{perf.completionRate == null ? NOT_ENOUGH_DATA : `${Math.round(perf.completionRate)}%`}</CellValue>
                <CellLabel>completion rate</CellLabel>
              </Cell>
            </StatCells>

            {perf.domains?.length > 0 && (
              <SubBlock>
                <SubHead>By area</SubHead>
                {perf.domains.map(d => (
                  <BarRow key={d.name}>
                    <BarName title={d.name}>{d.name}</BarName>
                    {d.onTimeRate == null ? (
                      <BarNone>none completed yet</BarNone>
                    ) : (
                      <>
                        <BarTrack><BarFill style={{ width: `${Math.max(3, d.onTimeRate)}%`, background: scoreColour(d.onTimeRate) }} /></BarTrack>
                        <BarPct>{Math.round(d.onTimeRate)}%</BarPct>
                      </>
                    )}
                  </BarRow>
                ))}
                <Hint>Share of your completed tasks in each area that met their deadline.</Hint>
              </SubBlock>
            )}

            {showVs && (
              <SubBlock>
                <SubHead>You vs the team</SubHead>
                <BarRow>
                  <BarName>You</BarName>
                  <BarTrack><BarFill style={{ width: `${Math.max(3, team.myOnTimeRate)}%`, background: scoreColour(team.myOnTimeRate) }} /></BarTrack>
                  <BarPct>{Math.round(team.myOnTimeRate)}%</BarPct>
                </BarRow>
                <BarRow>
                  <BarName>Team median</BarName>
                  <BarTrack><BarFill style={{ width: `${Math.max(3, team.medianOnTimeRate)}%`, background: '#94a3b8' }} /></BarTrack>
                  <BarPct>{Math.round(team.medianOnTimeRate)}%</BarPct>
                </BarRow>
                <Hint>Your on-time rate vs the median of everyone working on your buildings.</Hint>
              </SubBlock>
            )}
          </PerfBody>
        )}
      </Panel>

      {awaitingSignoff.length > 0 && (
        <Panel>
          <PanelHead><FactCheckOutlinedIcon style={{ fontSize: 16 }} />Awaiting your sign-off</PanelHead>
          <List>{awaitingSignoff.map(TaskRow)}</List>
        </Panel>
      )}

      <Panel>
        <PanelHead><TodayOutlinedIcon style={{ fontSize: 16 }} />To action today</PanelHead>
        {today.length === 0 ? <Empty>Nothing due today. You’re on top of it.</Empty> : (
          <List>
            {today.slice(0, todayVisible).map(TaskRow)}
            <InfiniteScrollSentinel
              hasMore={today.length > todayVisible}
              loading={false}
              onLoadMore={() => setTodayVisible(v => v + PAGE)}
            />
          </List>
        )}
      </Panel>

      <Panel>
        <PanelHead><DateRangeOutlinedIcon style={{ fontSize: 16 }} />Coming up this week</PanelHead>
        {week.length === 0 ? <Empty>Nothing scheduled this week.</Empty> : (
          <List>{week.map(TaskRow)}</List>
        )}
      </Panel>

      <Panel>
        <PanelHead><DateRangeOutlinedIcon style={{ fontSize: 16 }} />Coming up this month</PanelHead>
        {month.length === 0 ? <Empty>Nothing else scheduled in the next month.</Empty> : (
          <List>{month.map(TaskRow)}</List>
        )}
      </Panel>

      <Panel>
        <PanelHead><BoltOutlinedIcon style={{ fontSize: 16 }} />My remedials</PanelHead>
        <PanelHint>Remedials you own, or where you are assigned to one of their tasks, soonest due first.</PanelHint>
        {remLoading && remedials.length === 0 && <SkeletonRows rows={3} widths={['50%', '20%', '16%']} />}
        {!remLoading && remedials.length === 0 && (
          <Empty>Nothing is waiting on you. Remedials you own, or tasks you are assigned to, will show here.</Empty>
        )}
        {remedials.length > 0 && (
          <>
            {remMeta.total != null && <CountLine>Showing {remedials.length} of {remMeta.total}</CountLine>}
            <List>
              {remedials.map(r => {
                const sev = severityMeta(r.severity)
                const due = dueInfo(r)
                const canConvert = r.permissions?.canConvert && r.stage === 'needs_decision'
                return (
                  <RemedialRow key={r.id}>
                    <RemedialMain role='button' tabIndex={0}
                      onClick={() => openRemedial(r.id)}
                      onKeyDown={e => { if (e.key === 'Enter') openRemedial(r.id) }}>
                      <Severity $bg={sev.bg} $fg={sev.color}>{sev.label}</Severity>
                      <ItemMain>
                        <ItemTitle>{r.title}</ItemTitle>
                        <ItemMeta>
                          {[r.buildingPassport?.name, due.label].filter(Boolean).join(' · ')}
                        </ItemMeta>
                      </ItemMain>
                    </RemedialMain>
                    {canConvert && (
                      <RemedialCta type='button' onClick={() => setConvertTarget(r)}>Create task</RemedialCta>
                    )}
                  </RemedialRow>
                )
              })}
              <InfiniteScrollSentinel
                hasMore={remMeta.currentPage < remMeta.lastPage}
                loading={remLoading}
                onLoadMore={() => fetchRemedials(remMeta.currentPage + 1)}
                label='Loading more remedials…'
              />
            </List>
          </>
        )}
      </Panel>

      <ConvertToTaskDialog
        action={convertTarget}
        open={!!convertTarget}
        onClose={() => setConvertTarget(null)}
        onConverted={() => onConverted(convertTarget?.id)}
      />
    </Grid>
  )
}

export default TeamMemberView

const Grid = styled.div`display:flex;flex-direction:column;gap:16px;`
const TopBar = styled.div`display:flex;justify-content:flex-end;`
const Stats = styled.div`display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:10px;@media (max-width:900px){grid-template-columns:repeat(auto-fit,minmax(150px,1fr));}`
const StatValue = styled.div`font-size:24px;font-weight:800;font-variant-numeric:tabular-nums;line-height:1.1;color:${({ $tone }) => $tone === 'bad' ? '#b00020' : '#0C1229'};`
const StatLabel = styled.div`margin-top:3px;font-size:11.5px;font-weight:600;color:#64748b;line-height:1.3;`
const IconChip = styled.span`
  flex-shrink:0;display:inline-flex;align-items:center;justify-content:center;
  width:38px;height:38px;border-radius:10px;
  background:${({ $tint }) => `${$tint}14`};
  color:${({ $tint }) => $tint};
`
const StatText = styled.span`flex:1;min-width:0;display:flex;flex-direction:column;`
const Chevron = styled.span`
  flex-shrink:0;display:inline-flex;align-items:center;color:#cbd5e1;
  transition:transform 140ms ease, color 140ms ease;
`
const StatTile = styled.div`
  font:inherit;text-align:left;width:100%;cursor:pointer;
  display:flex;align-items:center;gap:11px;
  background:#fff;border:1px solid #ececec;border-radius:12px;padding:12px 13px;
  transition:border-color 140ms ease, background-color 140ms ease;
  &:hover { border-color:#cbd5e1; background:#fafbfc; }
  &:hover ${Chevron} { transform:translateX(2px); color:#94a3b8; }
  &:focus-visible { outline:2px solid #33becc; outline-offset:2px; }
`
const CreateTask = styled.button`
  font:inherit;cursor:pointer;flex-shrink:0;align-self:flex-start;
  display:inline-flex;align-items:center;gap:6px;
  background:${({ theme }) => theme.colors.secondary};color:#fff;border:none;border-radius:10px;
  padding:11px 18px;font-size:14px;font-weight:700;
  box-shadow:0 1px 2px rgba(0,0,0,0.08);
  transition:filter 140ms ease;
  &:hover { filter:brightness(1.06); }
`
const Panel = styled.section`background:#fff;border:1px solid #ececec;border-radius:14px;padding:18px 20px;`
const PanelHead = styled.h3`display:flex;align-items:center;gap:8px;margin:0 0 12px;font-size:12px;font-weight:800;letter-spacing:0.08em;text-transform:uppercase;color:#64748b;svg{color:#94a3b8;}`
const Big = styled.div`font-size:40px;font-weight:300;font-variant-numeric:tabular-nums;color:${({ $tint }) => $tint};line-height:1;`
const PerfBody = styled.div`display:flex;flex-direction:column;gap:16px;`
const PerfHero = styled.div`display:flex;align-items:center;gap:14px;flex-wrap:wrap;`
const HeroMeta = styled.div`display:flex;flex-direction:column;gap:5px;align-items:flex-start;`
const HeroLabel = styled.div`font-size:12px;font-weight:700;color:#64748b;`
const RankBadge = styled.div`
  margin-left:auto;display:inline-flex;align-items:center;
  padding:5px 12px;border-radius:999px;font-size:12px;font-weight:700;
  background:${({ $good }) => $good ? '#dcfce7' : '#fef3c7'};
  color:${({ $good }) => $good ? '#15803d' : '#b45309'};
`
const StatCells = styled.div`display:grid;grid-template-columns:repeat(3,1fr);gap:10px;`
const Cell = styled.div`display:flex;flex-direction:column;gap:2px;padding:10px 12px;border:1px solid #ececec;border-radius:10px;background:#fafbfc;`
const CellValue = styled.div`font-size:20px;font-weight:800;font-variant-numeric:tabular-nums;color:#0C1229;line-height:1.1;`
const CellLabel = styled.div`font-size:11px;font-weight:600;color:#64748b;`
const SubBlock = styled.div`display:flex;flex-direction:column;gap:8px;`
const SubHead = styled.div`font-size:12px;font-weight:800;letter-spacing:0.06em;text-transform:uppercase;color:#64748b;`
const BarRow = styled.div`display:flex;align-items:center;gap:10px;`
const BarName = styled.div`flex:0 0 130px;font-size:12.5px;font-weight:600;color:#0C1229;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;`
const BarTrack = styled.div`flex:1;height:8px;background:#f1f5f9;border-radius:999px;overflow:hidden;`
const BarFill = styled.div`height:100%;border-radius:999px;`
const BarPct = styled.div`flex:0 0 40px;text-align:right;font-size:12.5px;font-weight:700;font-variant-numeric:tabular-nums;color:#475569;`
const BarNone = styled.div`flex:1;font-size:12px;color:#94a3b8;font-style:italic;`
const StatusPill = styled.span`
  align-self:flex-start;display:inline-flex;align-items:center;
  margin-bottom:2px;padding:2px 10px;border-radius:999px;
  font-size:11px;font-weight:700;
  background:${({ $bg }) => $bg};color:${({ $fg }) => $fg};
`
const Hint = styled.p`margin:12px 0 0;font-size:12px;color:#94a3b8;line-height:1.5;`
const PanelHint = styled.p`
  margin: 0 0 8px;
  font-size: 14px;
  color: #475467;
`
const CountLine = styled.div`margin:0 0 10px;font-size:12px;color:#64748b;`
const List = styled.div`display:flex;flex-direction:column;gap:8px;`
const Item = styled.div`
  width:100%;text-align:left;font:inherit;cursor:pointer;
  display:flex;align-items:center;gap:12px;padding:10px 12px;border-radius:10px;
  border:1px solid ${({ $overdue }) => $overdue ? '#b0002033' : '#ececec'};
  background:${({ $overdue }) => $overdue ? '#fdecea' : '#fff'};
  transition:border-color 140ms ease, background-color 140ms ease;
  &:hover { border-color:#cbd5e1; background:${({ $overdue }) => $overdue ? '#fbe1dd' : '#fafbfc'}; }
`
const RemedialRow = styled.div`
  display:flex;align-items:center;gap:10px;padding:10px 12px;border-radius:10px;
  border:1px solid #ececec;background:#fff;
`
const RemedialMain = styled.div`
  flex:1;min-width:0;display:flex;align-items:center;gap:12px;cursor:pointer;border-radius:8px;
  &:hover :first-child { filter:brightness(0.97); }
`
const RemedialCta = styled.button`
  font:inherit;cursor:pointer;flex-shrink:0;
  background:${({ theme }) => theme.colors.secondary};color:#fff;border:none;border-radius:8px;
  padding:7px 12px;font-size:12px;font-weight:700;
  transition:filter 140ms ease;
  &:hover { filter:brightness(1.06); }
`
const ItemMain = styled.div`flex:1;min-width:0;`
const ItemTitle = styled.div`font-size:13px;font-weight:700;color:#0C1229;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;`
const ItemMeta = styled.div`font-size:12px;color:#64748b;`
const Due = styled.span`flex-shrink:0;font-size:12px;font-weight:700;color:${({ $overdue }) => $overdue ? '#b00020' : '#475569'};`
const Severity = styled.span`
  flex-shrink:0;display:inline-flex;align-items:center;
  font-size:10px;font-weight:700;letter-spacing:0.04em;text-transform:uppercase;
  padding:3px 9px;border-radius:999px;
  background:${({ $bg }) => $bg};color:${({ $fg }) => $fg};
`
const Empty = styled.div`padding:18px;text-align:center;font-size:13px;color:#64748b;border:1px dashed #ececec;border-radius:10px;`
